import { type SchemaListItem, TreeNode } from '@morph-mapper/types';
import { useSchemaBuilder } from '../../../hooks/use-schema';
import { PreConfiguration } from '@/types';
import {
  getEntries,
  getKeys,
  getValues,
  isNotUndefined,
} from '@morph-mapper/utils';
import { useNotify } from '../../../hooks/use-notify';
import { t } from 'i18next';
import { usePlugins } from '@morph-mapper/plugins';
import {
  deserializeSchema,
  RuntimeSchema,
} from '@morph-mapper/schema-serialization';

export const useTemplateBuilder = (operatorId: number) => {
  const { buildAppState } = useSchemaBuilder();
  const { config, serialize } = usePlugins();
  const { error } = useNotify();

  const findDiscardableIds = (
    schema: RuntimeSchema,
    entries: Record<string, TreeNode>,
  ) => {
    return schema.updateTemplateDiscardFields
      .map((fieldName) => {
        return getEntries(entries)
          .map(([id, entry]) => {
            if (entry.key === fieldName) {
              return id;
            }
          })
          .filter(isNotUndefined);
      })
      .flat();
  };

  const validateConfiguration = (
    preconfig: PreConfiguration,
    schema: RuntimeSchema,
  ) => {
    const availableKeys = getKeys(schema.config || {});

    getValues(preconfig).forEach(({ key }) => {
      if (!availableKeys.includes(key)) {
        throw new Error(
          `Key ${key} is not available in the schema configuration.`,
        );
      }
    });
  };

  const buildTemplate = (name: string, schemaRecord: SchemaListItem) => {
    const schemaResult = deserializeSchema(schemaRecord);
    if (schemaResult.isErr()) {
      error(t('message.error.schemaNotFound'));
      return;
    }

    const { domain, type, variant, config: configSnapshot } = schemaRecord;
    const runtimeSchema = schemaResult.value;

    config([], domain, variant);

    const { entries, preconfiguration } = buildAppState(runtimeSchema, {
      name,
      operatorId,
    });

    validateConfiguration(preconfiguration, runtimeSchema);
    const updateDiscardIds = findDiscardableIds(runtimeSchema, entries);

    return {
      operatorId,
      name,
      domain,
      type,
      variant,
      schemaId: schemaRecord.id,
      graphs: {},
      entries,
      config: {
        preconfiguration,
        configSnapshot,
        updateDiscardIds,
      },
      plugins: serialize(),
    };
  };

  return { buildTemplate };
};
